import React from 'react';
import { Plus } from 'lucide-react';
import { Location } from '../types';

// Utility function to extract city name from full location name
const getCityName = (fullName: string): string => {
  // Handle undefined/null names safely
  if (!fullName || typeof fullName !== 'string') {
    return 'Unknown Location';
  }
  // Split by comma and take the first part (city name)
  return fullName.split(',')[0].trim();
};

interface LocationQuickSelectorProps {
  location: Location;
  onLocationChange: (location: Location) => void;
  favoriteLocations: Location[];
  onOpenPanelWithLocation: () => void;
  loading?: boolean;
}

export function LocationQuickSelector({
  location,
  onLocationChange,
  favoriteLocations,
  onOpenPanelWithLocation,
  loading = false,
}: LocationQuickSelectorProps) {
  // Favorites are now independent Location objects, no need for recent searches logic

  // Filter out any invalid location objects
  const validFavoriteLocations = favoriteLocations.filter(
    (loc) => loc && typeof loc === 'object' && loc.name && typeof loc.name === 'string',
  );

  // If no valid favorites, show "Select favorites" message
  if (validFavoriteLocations.length === 0) {
    return (
      <button
        onClick={onOpenPanelWithLocation}
        className="px-2 py-1 rounded text-xs font-medium transition-all duration-200 text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400"
        disabled={loading}
      >
        Select favorite locations
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 w-full min-w-0 justify-start sm:justify-end">
      <div
        className="flex items-center gap-1 overflow-x-auto scrollbar-hide flex-1 min-w-0 sm:flex-row-reverse"
        style={{ maxHeight: '3rem' }}
      >
        {validFavoriteLocations.map((loc) => (
          <button
            key={loc.name}
            onClick={() => onLocationChange(loc)}
            className={`
              px-2 py-1 rounded text-xs font-medium transition-all duration-200 flex-shrink-0 whitespace-nowrap
              ${
                location.name === loc.name
                  ? 'text-pink-500 dark:text-pink-400'
                  : 'text-gray-600 dark:text-gray-300'
              }
            `}
            disabled={loading}
            title={loc.name}
          >
            <span className="truncate max-w-16">{getCityName(loc.name)}</span>
          </button>
        ))}
      </div>

      <button
        onClick={onOpenPanelWithLocation}
        className="group px-1.5 py-1 rounded text-xs font-medium transition-all duration-200 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 relative"
        disabled={loading}
      >
        <Plus className="h-3 w-3" />

        {/* Tooltip */}
        <div className="absolute invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 right-0 top-full mt-1 px-2 py-1 bg-black text-white text-xs rounded whitespace-nowrap z-50">
          Add more locations
        </div>
      </button>
    </div>
  );
}
